import { useCallback, useRef, MutableRefObject } from "react";
import { LegendKey } from "../../../../models/chart_model";
import { COST_UNIT } from "../../../../Units";
import { getTimeRange } from "../../../../pages/Dashboard/Exchange3/Chart";
import { monthNames } from "../../../../pages/Dashboard/PriceForcating/IexMonthly";
import { normalizeDisplayLabel } from "../../../../utility/displayText";
import { compareLegends } from "../utils/legendUtils";
import {
  extractTooltipSnapshot,
  TooltipSnapshot,
} from "../utils/tooltipSnapshot";
import type { PinCompareActions, PinCompareState } from "./usePinCompareState";

interface Params {
  tooltipFilter?: (item: any, param: any) => boolean;
  secondXDataKey?: string;
  debouncedData: any[];
  isTimeSlot?: boolean;
  blockDurationMinutes?: number;
  fontSize: number;
  isPriceForecast: boolean;
  legends: LegendKey[];
  hidePrice?: boolean;
  unit?: string;
  customLegendOrder?: string[];
  getDragTooltipState?: () => {
    active?: boolean;
    dataIndex?: number;
    seriesName?: string;
    accentColor?: string;
  } | null;
  getActiveSeries?: () => string | null;
  getChartInstance?: () => any;
  pinState?: PinCompareState;
  pinActions?: PinCompareActions;
  latestHoverRef?: MutableRefObject<TooltipSnapshot | null>;
  resolveDisplayName?: (seriesName: string, dataPoint: any) => string;
  resolveValue?: (
    value: number,
    seriesName: string,
    dataPoint: any,
    legend: any,
  ) => string | null | undefined;
  enableDoubleClickCompare?: boolean;
}

export function useTooltipFormatter({
  tooltipFilter,
  secondXDataKey,
  debouncedData,
  isTimeSlot,
  blockDurationMinutes,
  fontSize,
  isPriceForecast,
  legends,
  hidePrice,
  unit,
  customLegendOrder,
  getDragTooltipState,
  getActiveSeries,
  getChartInstance,
  pinState,
  pinActions,
  latestHoverRef,
  resolveDisplayName,
  resolveValue,
  enableDoubleClickCompare = true,
}: Params) {
  const lastRenderRef = useRef<{ key: string; html: string }>({
    key: "",
    html: "",
  });
  return useCallback(
    (params: any) => {
      // Tooltip uses appendTo:"body", so .remix-tt lives outside chart DOM.
      // Find it globally by uid stamped into rendered HTML; fall back to
      // in-chart lookup for non-appendTo cases.
      const chartUid = (() => {
        const inst = getChartInstance?.();
        return (inst as any)?.id ?? "";
      })();
      const findTooltipRoots = (): HTMLElement[] => {
        const out: HTMLElement[] = [];
        if (chartUid) {
          document
            .querySelectorAll(
              `.remix-tt .enhanced-tooltip[data-chart-uid="${chartUid}"]`,
            )
            .forEach((node) => {
              const root = (node as HTMLElement).closest(
                ".remix-tt",
              ) as HTMLElement | null;
              if (root) out.push(root);
            });
        }
        if (out.length === 0) {
          const dom = getChartInstance?.()?.getDom?.() as
            | HTMLElement
            | undefined;
          dom
            ?.querySelectorAll(".remix-tt")
            .forEach((n) => out.push(n as HTMLElement));
        }
        return out;
      };
      const findTooltipRoot = (): HTMLElement | null =>
        findTooltipRoots()[0] || null;

      const setTooltipVisibilityGlobal = (visible: boolean) => {
        const tooltipNodes = new Set<HTMLElement>();
        findTooltipRoots().forEach((el) => {
          tooltipNodes.add(el);
          const host = el.closest(".echarts-tooltip") as HTMLElement | null;
          if (host) tooltipNodes.add(host);
        });
        tooltipNodes.forEach((el) => {
          if (!visible) {
            el.style.setProperty("display", "none", "important");
            el.style.setProperty("opacity", "0", "important");
            el.style.setProperty("visibility", "hidden", "important");
            el.style.setProperty("pointer-events", "none", "important");
          } else {
            el.style.removeProperty("display");
            el.style.removeProperty("opacity");
            el.style.removeProperty("visibility");
            el.style.removeProperty("pointer-events");
          }
        });
      };
      const resetHoverFocus = () => {
        const r = findTooltipRoot() as any;
        if (r?.__resetHover) r.__resetHover();
      };
      const installHoverSync = () => {
        const root = findTooltipRoot();
        if (!root) return;
        if ((root as any).__hoverSyncInstalled) return;
        (root as any).__hoverSyncInstalled = true;
        let currentFocus: string | null = null;
        const highlight = (name: string | null) => {
          if (currentFocus === name) return;
          currentFocus = name;
          (root as any).__currentFocus = name;
          const inst = getChartInstance?.();
          if (!inst || inst.isDisposed?.()) return;
          const opt = inst.getOption?.();
          const series = Array.isArray(opt?.series) ? opt.series : [];

          // Use ECharts native highlight/downplay actions â€” they handle
          // emphasis state without touching original styles, so colors
          // always restore cleanly on downplay.
          const namedSeries = series.filter(
            (s: any) => s?.name && !String(s.name).startsWith("__"),
          );
          if (!name) {
            inst.dispatchAction({
              type: "downplay",
              seriesName: namedSeries.map((s: any) => s.name),
            });
          } else {
            inst.dispatchAction({
              type: "downplay",
              seriesName: namedSeries.map((s: any) => s.name),
            });
            inst.dispatchAction({ type: "highlight", seriesName: name });
          }
          root.setAttribute("data-focus", name ? "1" : "0");
        };
        (root as any).__resetHover = () => highlight(null);
        root.addEventListener("mousemove", (e) => {
          const target = e.target as HTMLElement;
          const item = target.closest?.(".tooltip-item") as HTMLElement | null;
          if (!item) {
            highlight(null);
            return;
          }
          const name = item.getAttribute("data-series-name");
          highlight(name || null);
        });
        root.addEventListener("mouseleave", () => highlight(null));
      };
      requestAnimationFrame(installHoverSync);

      const snap = extractTooltipSnapshot(params, {
        legends,
        unit,
        hidePrice,
        isTimeSlot,
        blockDurationMinutes,
        isPriceForecast,
        secondXDataKey,
        debouncedData,
        customLegendOrder,
        tooltipFilter,
        resolveDisplayName,
        resolveValue,
      });
      if (latestHoverRef) latestHoverRef.current = snap;

      if (pinState?.isCompareMode && pinActions) {
        pinActions.setHoverPoint(snap);
      }

      const ttRootForReset = findTooltipRoot() as any;

      const cacheKey = (() => {
        const arr = Array.isArray(params) ? params : [params];
        const first = arr[0];
        if (!first) return "";
        const dragState = getDragTooltipState?.();
        const dragSig = dragState?.active
          ? `${dragState.dataIndex}:${dragState.seriesName}`
          : "";
        const valSig = arr
          .map((p: any) => `${p?.seriesName ?? ""}=${p?.value ?? ""}`)
          .join("|");
        const compareSig = pinState?.isCompareMode ? "c1" : "c0";
        const activeSig = getActiveSeries?.() ?? "";
        return `${first.dataIndex}|${first.name}|${valSig}|${dragSig}|${compareSig}|${activeSig}`;
      })();
      if (cacheKey && cacheKey === lastRenderRef.current.key) {
        setTooltipVisibilityGlobal(true);
        resetHoverFocus();
        return lastRenderRef.current.html;
      }
      if (ttRootForReset?.__resetHover) ttRootForReset.__resetHover();

      const captureRoot = findTooltipRoot();
      const prevSnapshot = new Map<string, { value: number; top: number }>();
      if (captureRoot) {
        Array.from(captureRoot.querySelectorAll(".tooltip-item")).forEach(
          (el) => {
            const it = el as HTMLElement;
            const key = it.getAttribute("data-series-name") || "";
            if (!key) return;
            const valEl = it.querySelector(".value") as HTMLElement | null;
            const rawVal = valEl
              ? parseFloat(valEl.textContent?.replace(/,/g, "") || "0")
              : 0;
            prevSnapshot.set(key, {
              value: rawVal,
              top: it.getBoundingClientRect().top,
            });
          },
        );
      }
      const applyTransitions = () => {
        const r = findTooltipRoot();
        if (!r) return;
        const items = Array.from(
          r.querySelectorAll(".tooltip-item"),
        ) as HTMLElement[];
        items.forEach((it) => {
          const key = it.getAttribute("data-series-name") || "";
          const before = prevSnapshot.get(key);
          if (!before) return;
          const afterTop = it.getBoundingClientRect().top;
          const dy = before.top - afterTop;
          if (Math.abs(dy) > 0.5) {
            it.animate(
              [
                { transform: `translateY(${dy}px)` },
                { transform: "translateY(0)" },
              ],
              { duration: 280, easing: "cubic-bezier(0.22,1,0.36,1)" },
            );
          }
          const valEl = it.querySelector(".value") as HTMLElement | null;
          if (!valEl) return;
          const to = parseFloat(valEl.textContent?.replace(/,/g, "") || "0");
          const from = before.value;
          if (!isFinite(from) || !isFinite(to)) return;
          if (Math.abs(from - to) < 0.001) return;
          const dur = 260;
          const start = performance.now();
          const fmt = (n: number) =>
            n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
          const tick = (now: number) => {
            const t = Math.min(1, (now - start) / dur);
            const eased = 1 - Math.pow(1 - t, 3);
            valEl.textContent = fmt(from + (to - from) * eased);
            if (t < 1) requestAnimationFrame(tick);
            else valEl.textContent = fmt(to);
          };
          requestAnimationFrame(tick);
        });
      };
      requestAnimationFrame(applyTransitions);
      const setTooltipVisibility = (visible: boolean) => {
        const tooltipNodes = new Set<HTMLElement>();
        findTooltipRoots().forEach((el) => {
          tooltipNodes.add(el);
          const host = el.closest(".echarts-tooltip") as HTMLElement | null;
          if (host) tooltipNodes.add(host);
        });

        tooltipNodes.forEach((el) => {
          if (!visible) {
            el.style.setProperty("display", "none", "important");
            el.style.setProperty("opacity", "0", "important");
            el.style.setProperty("visibility", "hidden", "important");
            el.style.setProperty("pointer-events", "none", "important");
            return;
          }
          el.style.removeProperty("display");
          el.style.removeProperty("opacity");
          el.style.removeProperty("visibility");
          el.style.removeProperty("pointer-events");
        });
      };

      const param = Array.isArray(params) ? params[0] : params;
      let items = Array.isArray(params) ? params : [params];
      if (!param) {
        setTooltipVisibility(false);
        resetHoverFocus();
        return "";
      }
      if (tooltipFilter)
        items = items.filter((item) => tooltipFilter(item, param));

      const cleanName =
        typeof param.name === "string"
          ? normalizeDisplayLabel(param.name)
          : param.name;
      let title = cleanName;
      if (secondXDataKey && debouncedData[param.dataIndex]?.[secondXDataKey]) {
        title = isTimeSlot
          ? `${debouncedData[param.dataIndex][secondXDataKey]} - ${cleanName} (${getTimeRange(parseInt(param.name), blockDurationMinutes)})`
          : `${debouncedData[param.dataIndex][secondXDataKey]} - ${cleanName}`;
      } else if (isTimeSlot) {
        title =
          getTimeRange(parseInt(param.name), blockDurationMinutes) +
          ` (${param.name})`;
      }

      const sortedItems = [...items].sort((a, b) =>
        compareLegends(
          a.seriesName || "",
          b.seriesName || "",
          customLegendOrder,
        ),
      );
      const validItems = sortedItems.filter((item) => {
        if (item.seriesName?.startsWith("__")) return false;
        if (item.value === null || item.value === undefined) return false;
        const value = parseFloat(item.value);
        return !isNaN(value) && isFinite(value);
      });

      if (validItems.length === 0) {
        setTooltipVisibility(false);
        resetHoverFocus();
        return "";
      }

      setTooltipVisibility(true);

      const now = new Date();
      const currentMonth = monthNames[now.getMonth()];
      const currentYear = now.getFullYear().toString();
      const dragTooltipState = getDragTooltipState?.() || null;
      const activeSeries = getActiveSeries?.() ?? null;

      const dataPointForResolver = debouncedData[param.dataIndex] ?? {};
      const tooltipItems = validItems.map((item) => {
        const year = item.seriesName?.trim();
        let displaySeriesName = item.seriesName;
        if (
          isPriceForecast &&
          item.name === currentMonth &&
          year === currentYear
        ) {
          displaySeriesName = `${currentYear} Forecast`;
        }
        if (resolveDisplayName) {
          displaySeriesName = resolveDisplayName(
            displaySeriesName,
            dataPointForResolver,
          );
        }

        const rawValue = parseFloat(item.value);
        const bgColor = item.color;
        const legend = legends.find((l) => l.name === item.seriesName);
        const customValue = resolveValue
          ? resolveValue(
              rawValue,
              item.seriesName,
              dataPointForResolver,
              legend,
            )
          : null;
        const value =
          customValue != null
            ? customValue
            : rawValue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        const isDragHighlight =
          !!dragTooltipState?.active &&
          dragTooltipState.dataIndex === param.dataIndex &&
          (item.seriesName === dragTooltipState.seriesName ||
            item.seriesId === dragTooltipState.seriesName);
        const dragAccent = dragTooltipState?.accentColor || "#E17A3A";

        let suffix = "";
        if (legend?.unit !== undefined) {
          suffix = legend.unit;
        } else {
          const suffixDict = {
            Price: COST_UNIT,
            Percent: "%",
            "%": "%",
            Cost: "Rs",
            Revenue: "Rs",
          };
          const foundKey = Object.keys(suffixDict).find((key) =>
            item.seriesName?.toString().includes(key),
          );
          suffix = foundKey
            ? suffixDict[foundKey as keyof typeof suffixDict]
            : legend?.unit || unit || "";
        }
        return {
          item,
          displaySeriesName,
          value,
          bgColor,
          isDragHighlight,
          dragAccent,
          suffix,
        };
      });

      type RenderedItem = {
        item: any;
        displaySeriesName: any;
        value: string;
        bgColor: any;
        isDragHighlight: boolean;
        dragAccent: string;
        suffix: string;
      };
      const buildItemHtml = (
        {
          item,
          displaySeriesName,
          value,
          bgColor,
          isDragHighlight,
          dragAccent,
          suffix,
        }: RenderedItem,
        isActive = false,
      ) => {
        const labelText = normalizeDisplayLabel(
          hidePrice
            ? displaySeriesName.replace("Price", "")
            : displaySeriesName,
        );
        // Split a trailing "(NN.NN UNIT)" quantum annotation onto its own
        // muted sub-line instead of cramming it into the series-name row.
        const quantumMatch = labelText.match(/^(.*?)\s*(\([^()]*\))\s*$/);
        const nameMain = quantumMatch ? quantumMatch[1] : labelText;
        const nameSub = quantumMatch ? quantumMatch[2] : "";
        const activeClass = isActive ? " tooltip-item--active" : "";
        const dragClass =
          isDragHighlight && !isActive ? " tooltip-item--drag" : "";
        return `<div class="tooltip-item${activeClass}${dragClass}" data-name="${displaySeriesName?.toLowerCase()}" data-series-name="${(item.seriesName || "").replace(/"/g, "&quot;")}" style="--accent:${bgColor}"><span class="color-indicator"></span><span class="series-name">${nameMain}${nameSub ? `<span class="series-name-sub">${nameSub}</span>` : ""}</span><span class="value-wrap"><span class="value" style="${isDragHighlight ? `color:${dragAccent};` : ""}">${value}</span><span class="unit">${suffix}</span></span></div>`;
      };

      const activeRendered = tooltipItems.find(
        (t) => activeSeries && t.item.seriesName === activeSeries,
      );
      const remainingRendered = tooltipItems.filter(
        (t) => !activeSeries || t.item.seriesName !== activeSeries,
      );

      const titleParts = (() => {
        const m = String(title).match(/^(.+?)\s*-\s*(\d+)\s*\(([^)]+)\)\s*$/);
        if (m) return { primary: m[1].trim(), slot: m[2], range: m[3].trim() };
        const m2 = String(title).match(/^(.+?)\s*\(([^)]+)\)\s*$/);
        if (m2) return { primary: m2[1].trim(), slot: "", range: m2[2].trim() };
        return { primary: String(title), slot: "", range: "" };
      })();
      const metaBits = [
        titleParts.slot ? `Block ${titleParts.slot}` : "",
        titleParts.range,
      ].filter(Boolean);

      const compareHint = !enableDoubleClickCompare
        ? ""
        : pinState?.isCompareMode
          ? "Compare mode on"
          : "";

      const headerHtml = `
        <div class="tooltip-header">
          <div class="tooltip-header-row">
            <span class="tooltip-kicker"><span class="tooltip-kicker-dot"></span>Snapshot</span>
            ${compareHint ? `<div class="tooltip-compare-hint">${compareHint}</div>` : ""}
          </div>
          <div class="tooltip-title-stack">
            <div class="tooltip-title">${titleParts.primary}</div>
            ${metaBits.length ? `<div class="tooltip-range"><svg class="tooltip-range-icon" viewBox="0 0 12 12" width="11" height="11" aria-hidden="true"><circle cx="6" cy="6" r="5" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M6 3.2V6l1.9 1.3" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>${metaBits.join(" · ")}</div>` : ""}
          </div>
        </div>`;

      const activeSectionHtml = activeRendered
        ? `<div class="tooltip-active-section"><div class="tooltip-section-label">Active Series</div>${buildItemHtml(activeRendered, true)}</div>`
        : "";
      const otherSectionLabel =
        activeRendered && remainingRendered.length > 0
          ? `<div class="tooltip-section-label tooltip-section-label--other">Other Series</div>`
          : "";
      const otherSectionHtml =
        remainingRendered.length > 0
          ? `${otherSectionLabel}<div class="tooltip-content">${remainingRendered.map((t) => buildItemHtml(t)).join("")}</div>`
          : "";

      const html = `<div class="enhanced-tooltip-shell"><div class="enhanced-tooltip" data-chart-uid="${chartUid}">${headerHtml}${activeSectionHtml}${otherSectionHtml}</div></div><style>.enhanced-tooltip-shell{padding:14px;margin:-14px;pointer-events:inherit}.enhanced-tooltip{background:rgba(255,255,255,0.92);backdrop-filter:saturate(140%) blur(14px);-webkit-backdrop-filter:saturate(140%) blur(14px);box-shadow:0 1px 2px rgba(15,23,42,0.06),0 8px 24px -6px rgba(15,23,42,0.18),0 24px 48px -12px rgba(15,23,42,0.16);border:1px solid rgba(15,23,42,0.08);border-radius:14px;font-family:'Inter','SF Pro Text',system-ui,-apple-system,sans-serif;min-width:280px;max-width:380px;padding:14px 14px 10px;color:#334155;letter-spacing:-0.005em;transition:opacity 120ms;transform-origin:center center;display:flex;flex-direction:column;max-height:min(400px,var(--tt-avail-h,400px));box-sizing:border-box;overflow:hidden}.tooltip-header,.tooltip-section-label,.tooltip-active-section{flex:0 0 auto}.tooltip-active-section{overflow:hidden}.remix-tt[data-mode="interactive"] .enhanced-tooltip{box-shadow:0 12px 32px -8px rgba(15,23,42,0.22)}.tooltip-header{display:flex;flex-direction:column;gap:8px;padding:0 2px 12px;margin-bottom:8px;border-bottom:1px dashed rgba(15,23,42,0.09)}.tooltip-header-row{display:flex;align-items:center;justify-content:space-between;gap:8px}.tooltip-kicker{display:inline-flex;align-items:center;gap:5px;font-size:${Math.max(fontSize - 3, 9)}px;font-weight:700;letter-spacing:0.12em;text-transform:uppercase;color:#64748b}.tooltip-kicker-dot{width:5px;height:5px;border-radius:50%;background:linear-gradient(135deg,#10b981,#059669);box-shadow:0 0 0 3px rgba(16,185,129,0.15)}.tooltip-title-stack{display:flex;flex-direction:column;gap:4px;min-width:0}.tooltip-title{font-weight:650;font-size:${fontSize + 5}px;color:#334155;line-height:1.15;letter-spacing:-0.02em;font-variant-numeric:tabular-nums;min-width:0;white-space:normal;overflow-wrap:anywhere;word-break:break-word}.tooltip-range{display:inline-flex;align-items:center;gap:6px;font-size:${Math.max(fontSize - 1, 11)}px;font-weight:500;color:#64748b;letter-spacing:0.005em;font-variant-numeric:tabular-nums}.tooltip-range-icon{color:#94a3b8;flex-shrink:0}.tooltip-compare-hint{display:inline-flex;align-self:flex-start;padding:2px 7px;border-radius:999px;background:rgba(15,118,110,0.08);color:#0f766e;font-size:${Math.max(fontSize - 3, 9)}px;font-weight:600;letter-spacing:0.01em}.tooltip-coachmark{display:inline-flex;align-items:center;gap:4px;align-self:flex-start;padding:3px 9px;border-radius:999px;background:linear-gradient(135deg,#6366f1,#8b5cf6);color:#fff;font-size:${Math.max(fontSize - 2, 10)}px;font-weight:600;letter-spacing:0.01em;box-shadow:0 3px 10px rgba(99,102,241,0.25);animation:coachmarkFade 3.5s forwards}@keyframes coachmarkFade{0%{opacity:0;transform:translateY(3px)}8%{opacity:1;transform:translateY(0)}85%{opacity:1;transform:translateY(0)}100%{opacity:0;transform:translateY(0);pointer-events:none}}.tooltip-section-label{font-size:${Math.max(fontSize - 3, 9)}px;font-weight:700;letter-spacing:0.1em;text-transform:uppercase;color:#94a3b8;padding:0 10px 4px;margin-top:2px}.tooltip-section-label--other{margin-top:8px;padding-top:8px;border-top:1px dashed rgba(15,23,42,0.09)}.tooltip-active-section{margin-bottom:4px}.tooltip-item--active{background:color-mix(in srgb,var(--accent) 8%,rgba(248,250,252,0.9));border-radius:10px;box-shadow:0 1px 4px rgba(15,23,42,0.06);padding:9px 10px 9px 12px}.tooltip-item--active:hover{background:color-mix(in srgb,var(--accent) 12%,rgba(248,250,252,0.95))}.tooltip-item--active .series-name{font-size:${fontSize}px;font-weight:600;color:#334155}.tooltip-item--active .value{font-weight:750;font-size:${fontSize + 4}px;color:#0f172a}.tooltip-item--active .unit{color:#475569;background:rgba(15,23,42,0.06)}.tooltip-item--active .color-indicator{width:4px;height:28px;border-radius:3px}.tooltip-content{flex:1 1 auto;min-height:0;overflow-y:auto;scrollbar-gutter:stable;margin:0 -4px;padding:2px 4px 0;scrollbar-width:thin;scrollbar-color:rgba(15,23,42,0.2) transparent;display:flex;flex-direction:column;gap:0}.tooltip-content::-webkit-scrollbar{width:6px}.tooltip-content::-webkit-scrollbar-thumb{background:rgba(15,23,42,0.18);border-radius:3px}.tooltip-content::-webkit-scrollbar-track{background:transparent}.tooltip-item{position:relative;display:flex;align-items:center;gap:10px;padding:7px 8px 7px 10px;border-radius:8px;transition:background 140ms ease}.tooltip-item + .tooltip-item{box-shadow:inset 0 1px 0 rgba(15,23,42,0.04)}.tooltip-item:hover{background:linear-gradient(90deg,color-mix(in srgb,var(--accent) 10%,transparent),color-mix(in srgb,var(--accent) 3%,transparent) 60%,transparent);box-shadow:none}.tooltip-item:hover + .tooltip-item{box-shadow:none}.tooltip-item--drag{background:rgba(225,122,58,0.1);box-shadow:inset 0 0 0 1px rgba(225,122,58,0.25)}.color-indicator{flex-shrink:0;width:3px;height:18px;border-radius:2px;background:linear-gradient(180deg,var(--accent),color-mix(in srgb,var(--accent) 75%,#000));box-shadow:0 0 0 1px color-mix(in srgb,var(--accent) 25%,transparent),0 0 10px color-mix(in srgb,var(--accent) 40%,transparent)}.series-name{flex:1;min-width:0;max-width:220px;white-space:normal;overflow-wrap:anywhere;word-break:break-word;line-height:1.25;font-size:${fontSize}px;font-weight:500;color:#64748b;letter-spacing:-0.005em;text-transform:capitalize;display:flex;flex-direction:column;gap:2px}.tooltip-item:hover .series-name{color:#334155;font-weight:600}.series-name-sub{font-size:${Math.max(fontSize - 2, 9)}px;font-weight:500;color:#94a3b8;text-transform:none;letter-spacing:-0.005em}.tooltip-item:hover .series-name-sub{color:#94a3b8}.value-wrap{display:inline-flex;align-items:baseline;gap:6px;margin-left:auto;padding-left:14px}.value{font-variant-numeric:tabular-nums;font-feature-settings:"tnum" 1,"cv11" 1;font-weight:650;font-size:${fontSize + 2}px;color:#334155;line-height:1;letter-spacing:-0.02em}.unit{font-size:${Math.max(fontSize - 1, 10)}px;font-weight:600;color:#64748b;letter-spacing:0.05em;text-transform:uppercase;align-self:center;transform:translateY(-1px);background:rgba(15,23,42,0.04);padding:1px 5px;border-radius:4px}.tooltip-item:hover .unit{color:#475569;background:rgba(15,23,42,0.07)}.tooltip-item--drag .value{font-weight:800;font-size:${fontSize + 2}px}.remix-tt[data-focus="1"] .tooltip-item{opacity:0.55;transition:opacity 120ms ease}.remix-tt[data-focus="1"] .tooltip-item:hover{opacity:1}</style>`;
      if (cacheKey) lastRenderRef.current = { key: cacheKey, html };
      return html;
    },
    [
      tooltipFilter,
      secondXDataKey,
      debouncedData,
      isTimeSlot,
      fontSize,
      isPriceForecast,
      legends,
      hidePrice,
      unit,
      customLegendOrder,
      getDragTooltipState,
      getActiveSeries,
      getChartInstance,
      pinState,
      pinActions,
      latestHoverRef,
      resolveDisplayName,
      resolveValue,
      enableDoubleClickCompare,
      blockDurationMinutes,
    ],
  );
}
